import React, { useEffect, useState } from 'react';
import { Button, Form, Input, Row } from 'antd';
import queryString from 'query-string';
import { RouteComponentProps } from 'react-router-dom';
import { LoadingSpin } from 'common/components/LoadingSpin';
import { useFormMapper } from 'common/helpers/form.helper';
import { ERoutesPublic } from 'common/models/routesModel';
import { formRules } from 'common/const/common.const';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { registerFranchisee } from 'app/store/reducers/auth.reducer';
import { userTransport } from 'entities/User/User.transport';
import { IFranchiseeRegistrationInfo } from 'entities/User/User.models';

// Franchisee self-registration via a Leslie-issued permanent guid link:
// /franchisee/register?guid=<guid>. The guid is validated live against the Leslie feed on load
// and again on submit; email comes from the feed (shown masked), the user only sets profile + password.
export const FranchiseeRegisterPage: React.FunctionComponent<RouteComponentProps> = ({ history }) => {
  const dispatch = useAppDispatch();
  const { authModelError, authModelLoading, authModelParams } = useAppSelector((state) => state.auth);

  const guid = (queryString.parse(history.location.search).guid as string) || '';
  const [info, setInfo] = useState<IFranchiseeRegistrationInfo | null>(null);
  const [infoLoading, setInfoLoading] = useState<boolean>(true);
  const [infoError, setInfoError] = useState<boolean>(false);

  const { fields } = useFormMapper(['firstName', 'lastName', 'phone', 'password'], null, authModelParams, authModelError);
  const isAlreadyRegistered = (authModelError as { status?: number } | null)?.status === 409;

  useEffect(() => {
    if (!guid) {
      setInfoLoading(false);
      setInfoError(true);
      return;
    }

    userTransport
      .getFranchiseeRegistrationInfo(guid)
      .then(setInfo)
      .catch(() => setInfoError(true))
      .finally(() => setInfoLoading(false));
  }, [guid]);

  const onFinish = (values: any) => {
    const { firstName, lastName, phone, password } = values;

    dispatch(registerFranchisee({ guid, firstName, lastName, phone, password }));
  };

  if (infoLoading) {
    return <LoadingSpin />;
  }

  if (infoError) {
    return (
      <Row justify="center" align="middle">
        <span className="t-align-c fs-sm font-weight-5 font-family__default">This registration link is not valid.</span>
        <span className="t-align-c">Please contact Minute Burger to request a new registration link.</span>
      </Row>
    );
  }

  return (
    <>
      <Row justify="center" className="mb-4">
        <h1 className="basic__text_title fs-md t-align-c" title="Welcome to MB Go! Complete your franchisee registration">
          Welcome to MB Go!
          <br /> Complete your franchisee registration
        </h1>
      </Row>
      {info && (
        <Row justify="center" className="mb-4">
          <span className="t-align-c fs-xs">
            {info.storeName ? `${info.storeName} — ` : ''}
            {info.maskedEmail}
          </span>
        </Row>
      )}
      {isAlreadyRegistered && (
        <Row justify="center" className="mb-4">
          <span className="t-align-c fs-xs">This franchisee is already registered.</span>
          <Button type="link" onClick={() => history.push(ERoutesPublic.Login)}>
            Go to login
          </Button>
        </Row>
      )}
      <Form onFinish={onFinish} fields={fields} layout="vertical">
        <Form.Item
          rules={[
            { required: true, message: 'First name is required' },
            { min: 2, message: 'First name must be at least 2 characters' },
            { max: 64, message: 'First name must be shorter than or equal 64 characters' },
          ]}
          name="firstName"
          label="First name"
        >
          <Input />
        </Form.Item>
        <Form.Item
          rules={[
            { required: true, message: 'Last name is required' },
            { min: 2, message: 'Last name must be at least 2 characters' },
            { max: 64, message: 'Last name must be shorter than or equal 64 characters' },
          ]}
          name="lastName"
          label="Last name"
        >
          <Input />
        </Form.Item>
        <Form.Item
          rules={[
            {
              required: true,
              message: 'Phone is required',
            },
            formRules.correctPhone,
          ]}
          name="phone"
          label="Phone"
        >
          <Input name="Phone" type="tel" placeholder="+63 9XX XXX XXXX"/>
        </Form.Item>
        <Form.Item
          rules={[
            { required: true, message: 'Password is required' },
            { min: 8, message: 'Password must be at least 8 characters' },
            { max: 16, message: 'Password must be shorter than or equal 16 characters' },
          ]}
          name="password"
          label="Password"
        >
          <Input.Password type="password" />
        </Form.Item>
        <Form.Item>
          <Button htmlType="submit" block type="primary" disabled={authModelLoading} loading={authModelLoading} title="Submit">
            Submit
          </Button>
        </Form.Item>
      </Form>
    </>
  );
};
